import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { TrendingUp, TrendingDown, Minus, BookOpen, Target } from 'lucide-react';
import { useChildPerformance } from '@/hooks/useChildPerformance';
import { Skeleton } from '@/components/ui/skeleton';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface ChildPerformanceCardProps {
  childName: string;
  childEmail: string;
}

export function ChildPerformanceCard({ childName, childEmail }: ChildPerformanceCardProps) {
  const { performance, isLoading } = useChildPerformance(childEmail);

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-6 w-48" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    );
  }

  if (!performance || performance.totalTests === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            {childName}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground">No test data available yet.</p>
        </CardContent>
      </Card>
    );
  }

  const getTrendIcon = () => {
    switch (performance.trend) {
      case 'improving':
        return <TrendingUp className="h-4 w-4 text-green-600" />;
      case 'declining':
        return <TrendingDown className="h-4 w-4 text-red-600" />;
      default:
        return <Minus className="h-4 w-4 text-gray-600" />;
    }
  };

  const getTrendColor = () => {
    switch (performance.trend) {
      case 'improving':
        return 'text-green-600';
      case 'declining':
        return 'text-red-600';
      default:
        return 'text-gray-600';
    }
  };

  const chartData = performance.recentScores.map((score, index) => ({
    test: `Test ${performance.recentScores.length - index}`,
    score: Math.round(score)
  })).reverse();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <BookOpen className="h-5 w-5" />
            {childName}
          </span>
          <Badge variant={performance.overallAverage >= 70 ? 'default' : 'secondary'}>
            {performance.overallAverage}% Average
          </Badge>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">Total Tests</p>
            <p className="text-2xl font-bold">{performance.totalTests}</p>
          </div>
          <div className="space-y-1">
            <p className="text-sm text-muted-foreground">Trend</p>
            <p className={`text-lg font-semibold flex items-center gap-2 ${getTrendColor()}`}>
              {getTrendIcon()}
              {performance.trend.charAt(0).toUpperCase() + performance.trend.slice(1)}
            </p>
          </div>
          {performance.lastTestDate && (
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Last Test</p>
              <p className="text-sm font-medium">
                {new Date(performance.lastTestDate).toLocaleDateString()}
              </p>
            </div>
          )}
        </div>

        {/* Subject Performance */}
        {Object.keys(performance.subjectPerformance).length > 0 && (
          <div className="space-y-3">
            <h4 className="text-sm font-semibold flex items-center gap-2">
              <Target className="h-4 w-4" />
              Subject Performance
            </h4>
            <div className="space-y-2">
              {Object.entries(performance.subjectPerformance).map(([subject, score]) => (
                <div key={subject} className="flex items-center justify-between">
                  <span className="text-sm">{subject}</span>
                  <Badge variant={score >= 70 ? 'default' : 'secondary'}>
                    {Math.round(score)}%
                  </Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recent Performance Chart */}
        {chartData.length > 0 && (
          <div className="space-y-3">
            <h4 className="text-sm font-semibold">Recent Performance</h4>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="test" fontSize={12} />
                <YAxis domain={[0, 100]} fontSize={12} />
                <Tooltip />
                <Line 
                  type="monotone" 
                  dataKey="score" 
                  stroke="hsl(var(--primary))" 
                  strokeWidth={2}
                  dot={{ fill: "hsl(var(--primary))" }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
